import { useEffect, useState } from "react"

import { Color } from "@tiptap/extension-color"
import Image from "@tiptap/extension-image"
import ListItem from "@tiptap/extension-list-item"
import TextAlign from "@tiptap/extension-text-align"
import { TextStyle } from "@tiptap/extension-text-style"
import { Editor, EditorProvider, useCurrentEditor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  Code,
  CornerDownLeft,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Heading6,
  Image as ImageIcon,
  Italic,
  List,
  ListOrdered,
  Pilcrow,
  Quote,
  Redo2,
  Strikethrough,
  Undo2,
} from "lucide-react"

import "./styles.scss"

interface MenuBarProps {
  editor: Editor
}

const MenuBar = ({ editor }: MenuBarProps) => {
  if (!editor) {
    return null
  }

  const buttonClass =
    "p-2 rounded hover:bg-palette-jungleGreen200 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
  const activeButtonClass = "bg-palette-jungleGreen200"

  return (
    <div className="bg-white border border-palette-jungleGreen300 rounded-md p-1 shadow-sm mb-2">
      <div className="flex flex-wrap items-center gap-1">
        {/* Text formatting group */}
        <div className="flex border-r border-palette-jungleGreen300 pr-1 mr-1">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              editor.chain().focus().toggleBold().run()
            }}
            disabled={!editor.can().chain().focus().toggleBold().run()}
            className={`${buttonClass} ${editor.isActive("bold") ? activeButtonClass : ""}`}
            title="Bold"
          >
            <Bold className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              editor.chain().focus().toggleItalic().run()
            }}
            disabled={!editor.can().chain().focus().toggleItalic().run()}
            className={`${buttonClass} ${editor.isActive("italic") ? activeButtonClass : ""}`}
            title="Italic"
          >
            <Italic className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              editor.chain().focus().toggleStrike().run()
            }}
            disabled={!editor.can().chain().focus().toggleStrike().run()}
            className={`${buttonClass} ${editor.isActive("strike") ? activeButtonClass : ""}`}
            title="Strikethrough"
          >
            <Strikethrough className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              editor.chain().focus().toggleCode().run()
            }}
            disabled={!editor.can().chain().focus().toggleCode().run()}
            className={`${buttonClass} ${editor.isActive("code") ? activeButtonClass : ""}`}
            title="Code"
          >
            <Code className="w-4 h-4" />
          </button>
        </div>

        {/* Paragraph formatting group */}
        <div className="flex border-r border-palette-jungleGreen300 pr-1 mr-1">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              editor.chain().focus().setParagraph().run()
            }}
            className={`${buttonClass} ${editor.isActive("paragraph") ? activeButtonClass : ""}`}
            title="Paragraph"
          >
            <Pilcrow className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              editor.chain().focus().toggleHeading({ level: 1 }).run()
            }}
            className={`${buttonClass} ${
              editor.isActive("heading", { level: 1 }) ? activeButtonClass : ""
            }`}
            title="Heading 1"
          >
            <Heading1 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }}
            className={`${buttonClass} ${
              editor.isActive("heading", { level: 2 }) ? activeButtonClass : ""
            }`}
            title="Heading 2"
          >
            <Heading2 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              editor.chain().focus().toggleHeading({ level: 3 }).run()
            }}
            className={`${buttonClass} ${
              editor.isActive("heading", { level: 3 }) ? activeButtonClass : ""
            }`}
            title="Heading 3"
          >
            <Heading3 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              editor.chain().focus().toggleHeading({ level: 4 }).run()
            }}
            className={`${buttonClass} ${
              editor.isActive("heading", { level: 4 }) ? activeButtonClass : ""
            }`}
            title="Heading 4"
          >
            <Heading4 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              editor.chain().focus().toggleHeading({ level: 5 }).run()
            }}
            className={`${buttonClass} ${
              editor.isActive("heading", { level: 5 }) ? activeButtonClass : ""
            }`}
            title="Heading 5"
          >
            <Heading5 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              editor.chain().focus().toggleHeading({ level: 6 }).run()
            }}
            className={`${buttonClass} ${
              editor.isActive("heading", { level: 6 }) ? activeButtonClass : ""
            }`}
            title="Heading 6"
          >
            <Heading6 className="w-4 h-4" />
          </button>
        </div>

        {/* List formatting group */}
        <div className="flex border-r border-palette-jungleGreen300 pr-1 mr-1">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              editor.chain().focus().toggleBulletList().run()
            }}
            className={`${buttonClass} ${editor.isActive("bulletList") ? activeButtonClass : ""}`}
            title="Bullet List"
          >
            <List className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              editor.chain().focus().toggleOrderedList().run()
            }}
            className={`${buttonClass} ${editor.isActive("orderedList") ? activeButtonClass : ""}`}
            title="Ordered List"
          >
            <ListOrdered className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              editor.chain().focus().toggleBlockquote().run()
            }}
            className={`${buttonClass} ${editor.isActive("blockquote") ? activeButtonClass : ""}`}
            title="Blockquote"
          >
            <Quote className="w-4 h-4" />
          </button>
        </div>

        {/* Text alignment group */}
        <div className="flex border-r border-palette-jungleGreen300 pr-1 mr-1">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              editor.chain().focus().setTextAlign("left").run()
            }}
            className={`${buttonClass} ${
              editor.isActive({ textAlign: "left" }) ? activeButtonClass : ""
            }`}
            title="Align Left"
          >
            <AlignLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              editor.chain().focus().setTextAlign("center").run()
            }}
            className={`${buttonClass} ${
              editor.isActive({ textAlign: "center" }) ? activeButtonClass : ""
            }`}
            title="Align Center"
          >
            <AlignCenter className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              editor.chain().focus().setTextAlign("right").run()
            }}
            className={`${buttonClass} ${
              editor.isActive({ textAlign: "right" }) ? activeButtonClass : ""
            }`}
            title="Align Right"
          >
            <AlignRight className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              editor.chain().focus().setTextAlign("justify").run()
            }}
            className={`${buttonClass} ${
              editor.isActive({ textAlign: "justify" }) ? activeButtonClass : ""
            }`}
            title="Justify"
          >
            <AlignJustify className="w-4 h-4" />
          </button>
        </div>

        {/* Image and empty row group */}
        <div className="flex border-r border-palette-jungleGreen300 pr-1 mr-1">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              const url = window.prompt("Enter the URL of the image:")
              if (url) {
                editor.chain().focus().setImage({ src: url }).run()
              }
            }}
            className={buttonClass}
            title="Insert Image"
          >
            <ImageIcon className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              // Insert two empty paragraphs
              editor.chain().focus().setHardBreak().run()
            }}
            className={buttonClass}
            title="Insert Empty Rows"
          >
            <CornerDownLeft className="w-4 h-4" />
          </button>
        </div>

        {/* History group */}
        <div className="flex">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              editor.chain().focus().undo().run()
            }}
            disabled={!editor.can().chain().focus().undo().run()}
            className={`${buttonClass} ${
              !editor.can().chain().focus().undo().run() ? "opacity-50 cursor-not-allowed" : ""
            }`}
            title="Undo"
          >
            <Undo2 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              editor.chain().focus().redo().run()
            }}
            disabled={!editor.can().chain().focus().redo().run()}
            className={`${buttonClass} ${
              !editor.can().chain().focus().redo().run() ? "opacity-50 cursor-not-allowed" : ""
            }`}
            title="Redo"
          >
            <Redo2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

const extensions = [
  Color.configure({ types: [TextStyle.name, ListItem.name] }),
  TextStyle,
  StarterKit.configure({
    bulletList: {
      keepMarks: true,
      keepAttributes: false,
    },
    orderedList: {
      keepMarks: true,
      keepAttributes: false,
    },
  }),
  Image.configure({
    inline: true,
  }),
  TextAlign.configure({
    types: ["heading", "paragraph"],
    alignments: ["left", "center", "right", "justify"],
    defaultAlignment: "left",
  }),
]

interface ContractEditorProps {
  content?: string
  onChange?: (content: string) => void
}

export default function ContractEditor({ content = "", onChange }: ContractEditorProps) {
  const [editorContent, setEditorContent] = useState<string | null>(content)

  // Update internal state when prop changes
  useEffect(() => {
    setEditorContent(content)
  }, [content])

  // Handle editor content changes
  const handleUpdate = ({ editor }: { editor: Editor }) => {
    const newContent = editor.getHTML()
    setEditorContent(newContent)

    // Call the onChange prop if provided
    if (onChange) {
      onChange(newContent)
    }
  }
  return (
    <>
      <EditorProvider
        slotBefore={<MenuBarWrapper />}
        extensions={extensions}
        content={editorContent ?? ""}
        onUpdate={handleUpdate}
        editorProps={{
          attributes: {
            class:
              "border border-palette-jungleGreen300 rounded-md p-4 min-h-[200px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50",
          },
        }}
      ></EditorProvider>
    </>
  )
}

// Wrapper component to access editor from context
const MenuBarWrapper = () => {
  const { editor } = useCurrentEditor()

  if (!editor) {
    return null
  }

  return <MenuBar editor={editor} />
}
